import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Inventory, PriceCheck, Refresh } from "@mui/icons-material";
import { getProducts } from "@/features/products/services/productService";
import { logError } from "@/shared/utils/logger.js";
import AuthContext from "@/contexts/AuthContext.jsx";
import CreatePriceCardElementDialog from "./CreatePriceCardElementDialog.jsx";
import {
  getLivePriceCardElement,
  searchLivePriceCardElements,
} from "../services/priceCardElementService.js";
import {
  clearPriceCardSnapshot,
  compareLineItemToLiveElement,
  refreshLineItemFromLiveElement,
  snapshotPriceCardElement,
} from "../utils/lineItemPricing.js";

const CUSTOM_ID = "__CUSTOM__";
const MISSING_TARIFF_ID = "__MISSING_TARIFF__";

const normalizeProduct = (product) => ({
  ...product,
  id: product.id || product._id,
  source: "product",
});

const PriceCardLineItemEditor = ({
  lineItem,
  onLineItemChange,
  onCreateProduct,
  onMissingTariff,
  label = "Tariff, Product or Custom Item",
  required = false,
  disabled = false,
  includeProducts = true,
}) => {
  const auth = React.useContext(AuthContext);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [liveElement, setLiveElement] = useState(null);
  const [missingTariffRequest, setMissingTariffRequest] = useState(null);
  const snapshotPriceRef = useRef(null);
  const canCreateTariff = auth?.hasPermission?.("quotes:pricing") === true;
  const createMissingTariff =
    onMissingTariff ||
    (canCreateTariff
      ? (request) => setMissingTariffRequest(request)
      : null);

  const selectedValue = useMemo(() => {
    if (lineItem.priceCardElementId) {
      return {
        id: lineItem.priceCardElementId,
        name: lineItem.description,
        source: "priceCard",
      };
    }
    return lineItem.product
      ? { ...lineItem.product, source: "product" }
      : null;
  }, [lineItem]);

  useEffect(() => {
    if (selectedValue?.name) setInputValue(selectedValue.name);
  }, [selectedValue?.id, selectedValue?.name]);

  useEffect(() => {
    if (!lineItem.priceCardElementId) {
      snapshotPriceRef.current = null;
      return;
    }
    if (
      snapshotPriceRef.current == null ||
      snapshotPriceRef.current.elementId !== lineItem.priceCardElementId
    ) {
      snapshotPriceRef.current = {
        elementId: lineItem.priceCardElementId,
        unitPrice: Number(lineItem.unitPrice),
      };
      return;
    }
    const originalUnitPrice = snapshotPriceRef.current.unitPrice;
    if (
      Number(lineItem.unitPrice) !== originalUnitPrice &&
      lineItem.pricingOverride?.applied !== true
    ) {
      onLineItemChange({
        ...lineItem,
        pricingOverride: {
          applied: true,
          reason: "",
          originalUnitPrice,
        },
      });
    }
  }, [lineItem, onLineItemChange]);

  const search = useCallback(async (term) => {
    const query = term.trim();
    if (query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const searches = [searchLivePriceCardElements(query)];
      if (includeProducts) {
        searches.push(getProducts({ search: query, active: true }));
      }
      const settled = await Promise.allSettled(searches);
      const elements =
        settled[0].status === "fulfilled" ? settled[0].value : [];
      let products = [];
      if (includeProducts && settled[1]) {
        const productPayload =
          settled[1].status === "fulfilled" ? settled[1].value : null;
        products = productPayload?.success
          ? (productPayload.data || []).map(normalizeProduct)
          : [];
      }
      setOptions([
        ...elements.map((element) => ({ ...element, source: "priceCard" })),
        ...products,
      ]);
    } catch (error) {
      logError("Error searching tariffs and products", {
        search: query,
        error: error.message,
      });
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [includeProducts]);

  useEffect(() => {
    if (selectedValue) return undefined;
    const timeout = setTimeout(() => search(inputValue), 300);
    return () => clearTimeout(timeout);
  }, [inputValue, search, selectedValue]);

  const selectableOptions = useMemo(() => {
    if (inputValue.trim().length < 2 || selectedValue) return options;
    return [
      ...options,
      {
        id: CUSTOM_ID,
        name: `Use "${inputValue.trim()}" as a one-off item`,
        source: "custom",
      },
      ...(createMissingTariff
        ? [
            {
              id: MISSING_TARIFF_ID,
              name: `Request missing tariff "${inputValue.trim()}"`,
              source: "missing",
            },
          ]
        : []),
    ];
  }, [createMissingTariff, inputValue, options, selectedValue]);

  const selectOption = (_, option) => {
    setComparison(null);
    setLiveElement(null);

    if (!option) {
      onLineItemChange({
        ...clearPriceCardSnapshot(lineItem),
        product: null,
        productId: null,
        isCustom: true,
      });
      setInputValue("");
      return;
    }
    if (option.source === "missing") {
      createMissingTariff({
        name: inputValue.trim(),
        lineItem: { ...lineItem },
        onCreated: (element) =>
          onLineItemChange(snapshotPriceCardElement(lineItem, element)),
      });
      return;
    }
    if (option.source === "custom") {
      const description = inputValue.trim();
      if (onCreateProduct) {
        onCreateProduct(description);
      } else {
        onLineItemChange({
          ...clearPriceCardSnapshot(lineItem),
          product: null,
          productId: null,
          description,
          isCustom: true,
          persistProduct: true,
        });
      }
      setInputValue(description);
      return;
    }
    if (option.source === "priceCard") {
      snapshotPriceRef.current = {
        elementId: option.id,
        unitPrice: Number(option.unitPrice),
      };
      onLineItemChange(snapshotPriceCardElement(lineItem, option));
      return;
    }

    const quantity = Number(lineItem.quantity ?? 1);
    const unitPrice = option.unitPrice ?? lineItem.unitPrice ?? "";
    onLineItemChange({
      ...clearPriceCardSnapshot(lineItem),
      product: option,
      productId: option.id || null,
      description: option.name || lineItem.description,
      unitPrice,
      total:
        Math.round(quantity * Number(unitPrice === "" ? 0 : unitPrice) * 100) /
        100,
      isCustom: false,
      supplier:
        option.defaultSupplier ?? lineItem.supplier ?? "bubakii",
    });
  };

  const compareWithLive = async () => {
    setLoading(true);
    try {
      const element = await getLivePriceCardElement(
        lineItem.priceCardElementId
      );
      setLiveElement(element);
      setComparison(compareLineItemToLiveElement(lineItem, element));
    } catch (error) {
      setComparison({ status: "error", changes: [] });
      logError("Error comparing tariff element", { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const refreshFromLive = () => {
    if (!liveElement) return;
    snapshotPriceRef.current = {
      elementId: liveElement.id,
      unitPrice: Number(liveElement.unitPrice),
    };
    onLineItemChange(refreshLineItemFromLiveElement(lineItem, liveElement));
    setComparison({ status: "current", changes: [] });
  };

  return (
    <Stack spacing={1}>
      <Autocomplete
        disabled={disabled}
        value={selectedValue}
        inputValue={inputValue}
        options={selectableOptions}
        onChange={selectOption}
        onInputChange={(_, nextValue, reason) => {
          if (reason === "input" || reason === "clear") setInputValue(nextValue);
        }}
        filterOptions={(values) => values}
        getOptionLabel={(option) => option.name || ""}
        isOptionEqualToValue={(option, value) =>
          option.id === value?.id && option.source === value?.source
        }
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            placeholder="Search live tariffs, products, or enter a one-off"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={`${option.source}-${option.id}`}>
            {option.source === "priceCard" ? (
              <PriceCheck fontSize="small" sx={{ mr: 1 }} />
            ) : option.source === "product" ? (
              <Inventory fontSize="small" sx={{ mr: 1 }} />
            ) : (
              <Add fontSize="small" sx={{ mr: 1 }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">{option.name}</Typography>
              {option.description && option.description !== option.name && (
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              )}
            </Box>
            {option.source === "priceCard" && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`R ${Number(option.unitPrice || 0).toFixed(2)}${
                  option.unit ? ` / ${option.unit}` : ""
                }`}
              />
            )}
          </Box>
        )}
      />

      {lineItem.priceCardElementId && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={`Tariff snapshot${
              lineItem.priceCardVersion != null
                ? ` v${lineItem.priceCardVersion}`
                : ""
            }`}
          />
          <Button
            size="small"
            variant="text"
            onClick={compareWithLive}
            disabled={loading}
          >
            Compare with live
          </Button>
          {comparison?.status === "changed" && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              onClick={refreshFromLive}
            >
              Refresh from live
            </Button>
          )}
        </Stack>
      )}

      {comparison?.status === "current" && (
        <Alert severity="success">Saved tariff values match the live card.</Alert>
      )}
      {comparison?.status === "changed" && (
        <Alert severity="warning">
          The live tariff has changed. Refresh only if you want to replace the
          saved values.
        </Alert>
      )}
      {comparison?.status === "missing" && (
        <Alert severity="warning">
          This saved tariff is no longer present on the live card.
        </Alert>
      )}
      {comparison?.status === "error" && (
        <Alert severity="error">The live tariff could not be compared.</Alert>
      )}
      {lineItem.priceCardElementId &&
        lineItem.pricingOverride?.applied === true && (
          <Alert severity="warning">
            <Stack spacing={1}>
              <Typography variant="body2">
                This price differs from its card snapshot. A reason is required
                and the change will be audited.
              </Typography>
              <TextField
                size="small"
                label="Price override reason"
                required
                value={lineItem.pricingOverride.reason || ""}
                onChange={(event) =>
                  onLineItemChange({
                    ...lineItem,
                    pricingOverride: {
                      ...lineItem.pricingOverride,
                      applied: true,
                      reason: event.target.value,
                    },
                  })
                }
              />
            </Stack>
          </Alert>
        )}
      <CreatePriceCardElementDialog
        open={Boolean(missingTariffRequest)}
        initialName={missingTariffRequest?.name || ""}
        onClose={() => setMissingTariffRequest(null)}
        onCreated={(element) => {
          missingTariffRequest?.onCreated?.(element);
          setMissingTariffRequest(null);
        }}
      />
    </Stack>
  );
};

export default PriceCardLineItemEditor;

